import React, { useEffect, useState } from "react";
import { api, fmtINR, fmtNum, todayISO, fmtDate } from "../lib/api";
import { Snowflake, Trash2, ArrowDownToLine, ArrowUpFromLine, Plus } from "lucide-react";
import { toast } from "sonner";

const KG_ITEMS = ["Chicken Carcass", "Mutton Carcass", "Live Chicken", "Liver", "Kudal", "Suththu Kolupu", "Fat"];
const PIECE_ITEMS = ["Head", "Trotters", "Brain", "Lungs", "Suvarotti"];

export default function FreezerInOut() {
  const [direction, setDirection] = useState("in");
  const [date, setDate] = useState(todayISO());
  const [qtys, setQtys] = useState({});
  const [customs, setCustoms] = useState([]);
  const [notes, setNotes] = useState("");
  const [inventory, setInventory] = useState(null);
  const [movements, setMovements] = useState([]);

  const load = async () => {
    const [iRes, mRes] = await Promise.all([
      api.get("/freezer-inventory"),
      api.get("/freezer-movements?limit=40"),
    ]);
    setInventory(iRes.data);
    setMovements(mRes.data);
  };

  useEffect(() => {
    load();
  }, []);

  const keyOf = (item, unit) => `${item}__${unit}`;

  const buildItems = () => {
    const items = [];
    KG_ITEMS.forEach((it) => {
      const q = parseFloat(qtys[keyOf(it, "kg")]);
      if (q > 0) items.push({ item: it, unit: "kg", qty: q });
    });
    PIECE_ITEMS.forEach((it) => {
      const q = parseFloat(qtys[keyOf(it, "piece")]);
      if (q > 0) items.push({ item: it, unit: "piece", qty: q });
    });
    customs.forEach((c) => {
      const q = parseFloat(c.qty);
      if (c.label.trim() && q > 0) items.push({ item: c.label.trim(), unit: c.unit, qty: q });
    });
    return items;
  };

  const save = async () => {
    const items = buildItems();
    if (items.length === 0) {
      toast.error("Enter quantity for at least one product");
      return;
    }
    try {
      await api.post("/freezer-movements", { date, direction, items, notes });
      toast.success(
        `${items.length} item(s) ${direction === "in" ? "added to" : "taken from"} freezer`
      );
      setQtys({});
      setCustoms([]);
      setNotes("");
      load();
    } catch (e) {
      toast.error("Failed to save");
    }
  };

  const delMovement = async (m) => {
    if (!window.confirm(`Delete ${m.direction.toUpperCase()} entry: ${m.item} ${m.qty}?`)) return;
    await api.delete(`/freezer-movements/${m.id}`);
    load();
  };

  const qtyInput = (item, unit) => (
    <div key={item} className="flex items-center justify-between gap-2 px-3 py-2">
      <span className="text-sm truncate">{item}</span>
      <div className="flex items-center gap-1">
        <input
          type="number"
          step={unit === "piece" ? "1" : "0.01"}
          placeholder="0"
          value={qtys[keyOf(item, unit)] || ""}
          onChange={(e) => setQtys((q) => ({ ...q, [keyOf(item, unit)]: e.target.value }))}
          data-testid={`fio-qty-${item.replace(/ /g, "-")}`}
          className="w-24 border border-zinc-300 px-2 py-1.5 text-sm font-mono-num text-right focus:outline-none focus:ring-2 focus:ring-sky-600"
        />
        <span className="text-[10px] text-zinc-400 w-6">{unit === "piece" ? "pcs" : "kg"}</span>
      </div>
    </div>
  );

  const stockColor = (v) => (v < 0 ? "text-rose-600" : v === 0 ? "text-zinc-400" : "text-emerald-700");

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto page-enter" data-testid="freezer-inout-page">
      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 flex items-center gap-1">
          <Snowflake className="h-3 w-3" /> Per-Product Ledger
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tighter mt-1">
          Freezer In / Out
        </h1>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Entry form */}
        <div className="border border-zinc-200 bg-white p-5" data-testid="fio-form">
          <div className="grid grid-cols-2 gap-2 mb-4">
            <button
              onClick={() => setDirection("in")}
              data-testid="fio-dir-in"
              className={`py-3 text-xs uppercase tracking-wider border flex items-center justify-center gap-1 ${
                direction === "in"
                  ? "bg-emerald-600 border-emerald-600 text-white"
                  : "border-zinc-300 text-zinc-600 hover:bg-zinc-100"
              }`}
            >
              <ArrowDownToLine className="h-4 w-4" /> Stock IN (added)
            </button>
            <button
              onClick={() => setDirection("out")}
              data-testid="fio-dir-out"
              className={`py-3 text-xs uppercase tracking-wider border flex items-center justify-center gap-1 ${
                direction === "out"
                  ? "bg-rose-600 border-rose-600 text-white"
                  : "border-zinc-300 text-zinc-600 hover:bg-zinc-100"
              }`}
            >
              <ArrowUpFromLine className="h-4 w-4" /> Taken OUT / Sold
            </button>
          </div>

          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            data-testid="fio-date"
            className="w-full border border-zinc-300 px-3 py-2 text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-sky-600"
          />

          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 bg-zinc-50 border border-zinc-200 px-3 py-1.5">
            By Kilograms
          </div>
          <div className="border border-t-0 border-zinc-200 divide-y divide-zinc-100 mb-4">
            {KG_ITEMS.map((it) => qtyInput(it, "kg"))}
          </div>

          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 bg-zinc-50 border border-zinc-200 px-3 py-1.5">
            By Pieces
          </div>
          <div className="border border-t-0 border-zinc-200 divide-y divide-zinc-100 mb-4">
            {PIECE_ITEMS.map((it) => qtyInput(it, "piece"))}
          </div>

          {/* Custom items */}
          {customs.map((c, i) => (
            <div key={i} className="flex gap-1 mb-2">
              <input
                placeholder="Other product"
                value={c.label}
                onChange={(e) => setCustoms((arr) => arr.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))}
                data-testid={`fio-custom-label-${i}`}
                className="flex-1 border border-zinc-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-sky-600"
              />
              <input
                type="number"
                placeholder="Qty"
                value={c.qty}
                onChange={(e) => setCustoms((arr) => arr.map((x, j) => (j === i ? { ...x, qty: e.target.value } : x)))}
                data-testid={`fio-custom-qty-${i}`}
                className="w-20 border border-zinc-300 px-2 py-1.5 text-sm font-mono-num text-right focus:outline-none focus:ring-2 focus:ring-sky-600"
              />
              <select
                value={c.unit}
                onChange={(e) => setCustoms((arr) => arr.map((x, j) => (j === i ? { ...x, unit: e.target.value } : x)))}
                className="border border-zinc-300 px-2 py-1.5 text-sm"
              >
                <option value="kg">kg</option>
                <option value="piece">pcs</option>
              </select>
              <button
                onClick={() => setCustoms((arr) => arr.filter((_, j) => j !== i))}
                className="text-zinc-400 hover:text-rose-600 px-1"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
          <button
            onClick={() => setCustoms((arr) => [...arr, { label: "", qty: "", unit: "kg" }])}
            data-testid="fio-add-custom"
            className="text-xs uppercase tracking-wider text-sky-700 hover:underline flex items-center gap-1 mb-3"
          >
            <Plus className="h-3 w-3" /> Add other product
          </button>

          <input
            placeholder="Notes (optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            data-testid="fio-notes"
            className="w-full border border-zinc-300 px-3 py-2 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-sky-600"
          />

          <button
            onClick={save}
            data-testid="fio-save-btn"
            className={`w-full text-white py-3.5 font-display font-bold text-base uppercase tracking-wider active:scale-[0.98] transition-all ${
              direction === "in" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-rose-600 hover:bg-rose-700"
            }`}
          >
            {direction === "in" ? "Add Stock IN" : "Record Taken OUT"}
          </button>
        </div>

        {/* Inventory + movements */}
        <div className="space-y-6">
          <div className="border-2 border-zinc-900 bg-white" data-testid="fio-inventory">
            <div className="px-5 py-3 border-b border-zinc-200 flex items-center justify-between">
              <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                Current Freezer Inventory
              </span>
              {inventory && (
                <span className="text-xs font-mono-num font-bold">
                  {fmtNum(inventory.total_kg, 2)} kg · {inventory.total_pcs} pcs
                </span>
              )}
            </div>
            {!inventory || inventory.items.length === 0 ? (
              <div className="p-6 text-center text-sm text-zinc-500">
                No movements yet — record Stock IN to build inventory
              </div>
            ) : (
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-zinc-200 text-[10px] uppercase tracking-wider text-zinc-500">
                    <th className="px-4 py-2 text-left">Product</th>
                    <th className="px-4 py-2 text-right">In</th>
                    <th className="px-4 py-2 text-right">Out</th>
                    <th className="px-4 py-2 text-right font-bold">Stock</th>
                  </tr>
                </thead>
                <tbody>
                  {inventory.items.map((it) => (
                    <tr key={`${it.item}-${it.unit}`} className="border-b border-zinc-100" data-testid={`fio-inv-${it.item.replace(/ /g, "-")}`}>
                      <td className="px-4 py-2">
                        {it.item} <span className="text-[10px] text-zinc-400">({it.unit === "piece" ? "pcs" : "kg"})</span>
                      </td>
                      <td className="px-4 py-2 text-right font-mono-num text-emerald-700">{fmtNum(it.in_qty, 2)}</td>
                      <td className="px-4 py-2 text-right font-mono-num text-rose-600">{fmtNum(it.out_qty, 2)}</td>
                      <td className={`px-4 py-2 text-right font-mono-num font-bold ${stockColor(it.stock)}`}>
                        {fmtNum(it.stock, 2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="border border-zinc-200 bg-white" data-testid="fio-movements">
            <div className="px-5 py-3 border-b border-zinc-200 text-[10px] uppercase tracking-[0.2em] text-zinc-500">
              Recent Movements
            </div>
            {movements.length === 0 ? (
              <div className="p-6 text-center text-sm text-zinc-500">No entries yet</div>
            ) : (
              movements.map((m) => (
                <div key={m.id} className="border-b border-zinc-100 px-4 py-2 flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <span
                      className={`px-1.5 py-0.5 text-[10px] uppercase font-bold shrink-0 ${
                        m.direction === "in" ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-600"
                      }`}
                    >
                      {m.direction}
                    </span>
                    <span className="text-sm truncate">{m.item}</span>
                    <span className="text-xs text-zinc-500 shrink-0">{fmtDate(m.date)}</span>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="font-mono-num text-sm font-semibold">
                      {m.unit === "piece" ? `${Math.round(m.qty)} pcs` : `${fmtNum(m.qty, 2)} kg`}
                    </span>
                    <button
                      onClick={() => delMovement(m)}
                      data-testid={`fio-del-${m.id}`}
                      className="text-zinc-300 hover:text-rose-600"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
